/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';
import { Heart } from 'lucide-react';

import IconTag from './icon-tag';
import { parsePrice, planTier } from '../utils/renewal-plans';
import { ofertaDoPlano } from '../utils/termos-assinatura';
import { isInApp } from '../utils/inApp';

interface Props {
  onClose: () => void;
  isAnual: boolean;
  toggleAnual: () => void;
  onSelect: (planName: string, billingCycle: 'mensal' | 'anual') => void;
  plans: any;
  /**
   * Plano de contrato anual pago por mês (monthly_annual/annual_installment)
   * com que o cliente entrou. Presente = renovação nunca desce: sem aba
   * mensal, só anuais de nível ≥ e a opção de ficar no plano atual.
   */
  planoEntrada?: { name: string } | null;
}

interface UIPlan {
  name: string;
  /** Valor cobrado: o ANO no anual, o mês nos outros (utils/termos-assinatura). */
  price: string;
  /** "/ano" ou "/mês". */
  suffix: string;
  /** Só no anual: "em 12x de R$ 59,90 no cartão". */
  installment: string | null;
  originalPrice: string | null;
  /** Frequência + renovação, e fidelidade — por tipo de contrato do plano. */
  terms: string[];
  discount: string;
  color: string;
  textColor: string;
  buttonColor: string;
  highlight?: boolean;
  features: {
    name: string;
    included: boolean;
  }[];
}

import './scroll.css';

export default function ChangePlanModal({ onClose, onSelect, plans, planoEntrada }: Props) {
  const naWeb = !isInApp();
  const [plansData, setPlansData] = useState<{
    mensal: UIPlan[];
    anual: UIPlan[];
  }>({
    mensal: [],
    anual: [],
  });
  // Com planoEntrada a aba mensal nem existe: trocar pra mensal puro é descer.
  const [billingCycle, setBillingCycle] = useState<'mensal' | 'anual'>(planoEntrada ? 'anual' : 'mensal');

  // Nível desconhecido não entra (mesma régua de isRenewalPlanAllowed): sem
  // como provar que não desce, sobra só "continuar no plano atual".
  const nivelEntrada = planoEntrada ? planTier(planoEntrada.name) : null;
  const planosDoCiclo = planoEntrada
    ? plansData.anual.filter((plan) => {
        const nivel = planTier(plan.name);
        return nivel !== null && nivelEntrada !== null && nivel >= nivelEntrada;
      })
    : plansData[billingCycle];
  // Skeleton só enquanto o getAll() não resolveu — com planoEntrada a lista
  // filtrada pode ficar vazia de verdade e não pode virar skeleton eterno.
  const carregando = planoEntrada
    ? plansData.anual.length === 0 && plansData.mensal.length === 0
    : plansData[billingCycle].length === 0;

  function mapPlansToUIFormat(apiPlans: any[]): {
    mensal: UIPlan[];
    anual: UIPlan[];
  } {
    const result: { mensal: UIPlan[]; anual: UIPlan[] } = {
      mensal: [],
      anual: [],
    };

    for (const plan of apiPlans) {
      const ciclo = plan.billingCycle === 'monthly' ? 'mensal' : 'anual';

      // No app o anual destaca o valor COBRADO (o ano), com a parcela abaixo
      // (recusa da Google Play, 28/10/2026; mesma régua do app). Na web, o
      // valor do mês, como antes — o ano em destaque derrubou a conversão.
      const oferta = ofertaDoPlano(
        {
          billingCycle: plan.billingCycle,
          planType: plan.planType,
          precoTabela: parsePrice(plan.discountedPrice),
          precoOriginal: parsePrice(plan.originalPrice),
        },
        { anualPorMes: naWeb },
      );

      const formatted: UIPlan = {
        name: plan.name,
        price: oferta.preco,
        suffix: oferta.sufixo,
        // Card da web era sem linha de parcela.
        installment: naWeb ? null : oferta.parcela,
        originalPrice: oferta.precoOriginal,
        terms: oferta.termos,
        discount: `${plan.discountPercentage}% OFF`,
        color: 'white',
        textColor: getColor(plan.name),
        buttonColor: `bg-[${getColor(plan.name)}]`,
        highlight: plan.name === 'Aventureiro',
        features: plan.includedItems.map((item: any) => ({
          name: item.name,
          included: item.active,
        })),
      };

      result[ciclo].push(formatted);
    }

    return result;
  }

  function getColor(name: string) {
    if (name === 'Descoberta') return '#018ED5';
    if (name === 'Explorador') return '#5BB947';
    if (name === 'Aventureiro') return '#7C2EA3';
    return '#999999';
  }

  useEffect(() => {
    const formattedPlans = mapPlansToUIFormat(plans);
    setPlansData(formattedPlans);
    // [plans]: o modal pode abrir antes do getAll() resolver (auto-open via ?ref=)
  }, [plans]);

  return (
    <div className="fixed inset-0 z-50 bg-[#F5FAFF] flex items-center justify-center px-4 overflow-y-auto overflow-x-hidden">
      <div className="bg-[#F5FAFF] rounded-xl p-4 sm:p-8 w-full max-w-[95vw] sm:max-w-6xl relative overflow-auto max-h-[95vh] overflow-x-hidden scrollbar-none">
        <button onClick={onClose} className="absolute top-4 right-4 text-2xl text-gray-500 hover:text-gray-800">
          ×
        </button>
        <div className="flex items-center justify-center">
          <p className="text-center w-fit flex items-center justify-center px-6 h-10 bg-[#F6E6FF] text-sm text-[#7C2EA3] font-semibold mb-6 rounded-full">
            INVESTIMENTO NO FUTURO
          </p>
        </div>

        {/* Título */}
        <h2 className="text-4xl font-bold text-center mb-2 text-[#042B4E]">
          Escolha o plano ideal e transforme a <br /> sua relação com seus filhos
        </h2>

        {planoEntrada ? (
          // Ficar no plano atual é a primeira opção: o modal não pode sumir com ele
          <div className="mt-10 mb-8 mx-auto max-w-xl rounded-2xl bg-white shadow-md p-5 flex flex-col items-center gap-3 text-center">
            <div className="text-xs font-semibold tracking-[0.08em] text-[#595D69] uppercase">Seu plano atual</div>
            <button
              onClick={onClose}
              className="w-full py-3 px-6 rounded-full bg-[#018ED5] text-white font-bold cursor-pointer transition-transform hover:scale-[1.02]"
            >
              Continuar no meu plano atual ({planoEntrada.name})
            </button>
            {(carregando || planosDoCiclo.length > 0) && (
              <p className="text-sm text-[#595D69]">Ou escolha um plano anual abaixo:</p>
            )}
          </div>
        ) : (
          /* Toggle mensal/anual */
          <div className="flex mt-10 justify-center items-center gap-2 mb-8 bg-white rounded-full px-2 py-1 w-fit mx-auto shadow-sm">
            <button
              onClick={() => setBillingCycle('mensal')}
              className={`px-6 py-2 min-w-[140px] rounded-full h-11 font-medium transition-all cursor-pointer ${
                billingCycle === 'mensal' ? 'bg-[#018ED5] text-white' : 'text-gray-600'
              }`}
            >
              Mensal
            </button>
            <button
              onClick={() => setBillingCycle('anual')}
              className={`relative px-6 py-2 min-w-[140px] rounded-full font-medium transition-all cursor-pointer ${
                billingCycle === 'anual' ? 'bg-[#018ED5] text-white' : 'text-gray-600'
              }`}
            >
              Anual
              <span className="absolute -top-6 -right-2 text-[10px] font-bold text-pink-600">
                <img src="/descont.svg" />
              </span>
            </button>
          </div>
        )}

        {/* Cards — skeleton enquanto os planos carregam (evita piscada) */}
        {carregando ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 md:pt-12">
            {[0, 1, 2].map((i) => (
              <div key={i} className="rounded-2xl bg-white p-6 shadow-md animate-pulse">
                <div className="flex items-center justify-between mb-8">
                  <div className="h-7 w-32 bg-gray-200 rounded-md" />
                  <div className="h-7 w-20 bg-gray-100 rounded-md" />
                </div>
                <div className="h-4 w-28 bg-gray-100 rounded mb-3" />
                <div className="h-9 w-44 bg-gray-200 rounded mb-8" />
                <div className="h-12 w-full bg-gray-200 rounded-full mb-3" />
                <div className="h-3 w-40 bg-gray-100 rounded mx-auto mb-8" />
                <div className="h-11 w-full bg-blue-50 rounded-xl mb-6" />
                {[0, 1, 2, 3].map((j) => (
                  <div key={j} className="flex items-center gap-3 py-4 border-b border-[#DBECFF] last:border-0">
                    <div className="w-6 h-6 bg-gray-100 rounded-full shrink-0" />
                    <div className="h-4 flex-1 bg-gray-100 rounded" />
                  </div>
                ))}
              </div>
            ))}
          </div>
        ) : (
          <div
            className={`grid grid-cols-1 gap-6 items-start ${
              // Filtrado por nível sobram 1–2 cards: centraliza em vez de deixar coluna vazia
              planoEntrada && planosDoCiclo.length < 3
                ? planosDoCiclo.length === 1
                  ? 'max-w-sm mx-auto'
                  : 'sm:grid-cols-2 max-w-3xl mx-auto'
                : 'sm:grid-cols-2 md:grid-cols-3'
            }`}
          >
            {planosDoCiclo.map((plan) => (
              <div
                key={plan.name}
                className={`rounded-2xl bg-white relative overflow-hidden ${
                  plan.highlight ? 'shadow-xl ring-1 ring-[#7C2EA3]/25' : 'shadow-md md:mt-12'
                }`}
              >
                {plan.highlight && (
                  <div
                    className="flex items-center justify-center gap-2 py-3 text-white text-sm font-bold tracking-wider"
                    style={{
                      background: 'repeating-linear-gradient(115deg, #7C2EA3 0px, #7C2EA3 22px, #8636AE 22px, #8636AE 44px)',
                    }}
                  >
                    <span className="w-6 h-6 rounded-md bg-white/25 flex items-center justify-center">
                      <Heart className="w-3.5 h-3.5 fill-white text-white" />
                    </span>
                    PLANO MAIS ESCOLHIDO
                  </div>
                )}

                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-bold" style={{ color: plan.textColor }}>
                      {plan.name}
                    </h2>
                    <div
                      className="flex items-center gap-1.5 py-1.5 px-3 rounded-lg font-bold text-sm"
                      style={{ backgroundColor: `${plan.textColor}1A`, color: plan.textColor }}
                    >
                      <IconTag color={plan.textColor} />
                      {plan.discount}
                    </div>
                  </div>

                  {plan.originalPrice && (
                    <div className="text-lg text-[#9195A1] line-through">De {plan.originalPrice} por</div>
                  )}
                  <div className="text-4xl font-black mt-1" style={{ color: plan.textColor }}>
                    {plan.price}
                    <span className="text-2xl font-normal"> {plan.suffix}</span>
                  </div>
                  {plan.installment && (
                    <div className="text-base font-bold text-[#042B4E] mt-1">{plan.installment}</div>
                  )}

                  <button
                    onClick={() => onSelect?.(plan.name, billingCycle)}
                    className="w-full mt-6 py-3 rounded-full text-white text-lg font-bold cursor-pointer transition-transform hover:scale-[1.02]"
                    style={{ backgroundColor: plan.textColor, boxShadow: `0 0 0 4px ${plan.textColor}26` }}
                  >
                    Escolher plano
                  </button>
                  <div
                    className={
                      naWeb
                        ? 'mt-4 text-center text-xs font-semibold tracking-[0.08em] text-[#595D69] uppercase'
                        : 'mt-4 flex flex-col gap-1 text-center text-xs text-[#595D69]'
                    }
                  >
                    {plan.terms.map((linha) => (
                      <p key={linha}>{linha}</p>
                    ))}
                  </div>

                  <div className="mt-6 rounded-xl bg-gradient-to-b from-[#E0EFFF] to-[#E0EFFF]/20 py-2.5 px-4 flex items-center justify-center gap-3 text-[#0168AC] font-bold">
                    <span className="w-8 h-8 bg-[#0168AC]/15 flex items-center justify-center rounded-full">
                      <img src="/icon-tracking.svg" alt="" />
                    </span>
                    Frete grátis!
                  </div>

                  <div className="mt-6 font-semibold text-[#042B4E]">Incluso no plano:</div>
                  <ul className="text-[15px]">
                    {plan.features.map((feature, idx) => {
                      const isFirst = idx === 0;
                      return (
                        <li
                          key={idx}
                          className={`flex items-center gap-3 py-4 border-b border-[#DBECFF] last:border-0 ${
                            feature.included || isFirst ? 'text-[#042B4E] font-semibold' : 'text-gray-400'
                          }`}
                        >
                          <img
                            src={
                              isFirst ? '/icon-special.svg' : feature.included ? '/icon-check.svg' : '/icon-uncheck.svg'
                            }
                            alt={isFirst ? 'Destaque' : feature.included ? 'Incluído' : 'Não incluído'}
                            className="w-6 h-6 shrink-0"
                          />
                          <span>{feature.name}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
